import { ReactNode } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { useEventHistory, useOrderSummary } from "@app/data";
import { useIsDropoffTracking } from "@app/shared/context/DropoffTrackingContext";
import { useOrderErrorRedirect } from "@app/shared/hooks/useOrderErrorRedirect";
import { orderPaths } from "@app/shared/routing/orderPaths";
import { PageShell } from "@app/shared/layout";
import { Card, EmptyState, Skeleton } from "@app/design-system/components";
import { DriverCard } from "@app/features/shared/DriverCard";
import { LastEventCard } from "@app/features/shared/LastEventCard";
import { OrderDetailsHeader } from "./components/OrderDetailsHeader";
import { CustomerInfoRow } from "./components/CustomerInfoRow";
import { StopRow } from "./components/StopRow";
import { EntityInfoRow } from "./components/EntityInfoRow";
import { statusToneColor } from "@app/features/shared/statusTone/statusToneColor";
import { isCallDisabled } from "@app/features/shared/statusTone/isCallDisabled";
import { computeTrackingStatusLabel } from "@app/features/shared/statusTone/computeTrackingStatusLabel";
import { OrderSummary } from "@app/domain";
import { RootState } from "@app/store";
import { t } from "@app/utils/translation";
import styles from "./OrderDetailsPage.module.css";

export type OrderDetailsPageProps = {
  /**
   * Overrides the page's own header (back button + "Order Details" title +
   * code). Used only by `DesktopHomePage`, which reuses this page's body as
   * the desktop shell's root/"Main" screen — there's no "back" from the
   * root, so it swaps in the tenant logo instead (see `LogoTopBar`). Omit
   * for the normal back-button header (every other caller, unchanged).
   */
  renderHeader?: (args: { order: OrderSummary | undefined; isDropoffTracking: boolean; onBack: () => void }) => ReactNode;
};

/**
 * The hub page: everything about the order, plus every navigation entry
 * point into Pickup/Dropoff/Service/Vehicle Custom Fields. Reuses the same
 * `useOrderSummary` fetch as Home (see data/hooks) — arriving here from Home
 * never triggers a second network call.
 */
export const OrderDetailsPage = ({ renderHeader }: OrderDetailsPageProps = {}) => {
  const { trackCode = "" } = useParams();
  const navigate = useNavigate();
  const isDropoffTracking = useIsDropoffTracking();
  const language = useSelector((state: RootState) => state.setting.language);
  const { status, data: order, error } = useOrderSummary(trackCode, isDropoffTracking);
  useOrderErrorRedirect();

  // Ondemand orders show the Driver section; every other kind shows the
  // order's last event instead (same split as Home's BottomPanel) — extended
  // here with a total-events badge that navigates to Event History. Skip the
  // fetch entirely for Ondemand orders, mirroring `useLastEvent`'s gating.
  const isOnDemand = order?.kind === "ondemand";
  const { data: events } = useEventHistory(trackCode, !!order && !isOnDemand);
  const lastEvent = events && events.length > 0 ? events[events.length - 1] : null;
  const eventsCount = events?.length ?? 0;

  // Same status label Home's BottomPanel shows for this order — see
  // `computeTrackingStatusLabel` — so the two never disagree (e.g. Single
  // Drop-off Tracking's "Delivered" override once the tracked dropoff is
  // done, even while the order-level status still reads "PickedUp").
  const displayedStatusLabel = order
    ? computeTrackingStatusLabel({
        isDropoffTracking,
        statusCode: order.status.code,
        remainingQueuePosition: order.remainingQueuePosition,
        dropoffStatus: order.dropoffs[0]?.status ?? null,
        fallbackLabel: order.status.label,
        language,
      })
    : "";

  const goBack = () => navigate(orderPaths.home(trackCode, isDropoffTracking));

  return (
    <PageShell
      topBar={
        renderHeader ? (
          renderHeader({ order, isDropoffTracking, onBack: goBack })
        ) : (
          <OrderDetailsHeader orderCode={order?.code} isDropoffTracking={isDropoffTracking} onBack={goBack} />
        )
      }
    >
      {status === "loading" ? (
        <Card grouped>
          <Skeleton height={22} width="60%" />
          <div style={{ height: 12 }} />
          <Skeleton height={16} width="40%" />
        </Card>
      ) : status === "error" ? (
        <EmptyState title={t("CouldntLoadOrder", language)} description={error?.message} />
      ) : order ? (
        <>
          <CustomerInfoRow
            customerName={order.customer.name}
            customFieldsCount={order.customer.customFields.length}
            onClick={() => navigate(orderPaths.customerCustomFields(trackCode, isDropoffTracking))}
          />

          <Card grouped className={styles.statusSection} style={{ color: statusToneColor(order.status.tone) }}>
            {displayedStatusLabel}
          </Card>

          {isOnDemand ? (
            order.driver ? (
              <DriverCard
                driver={order.driver}
                grouped
                customFieldsCount={order.driver.customFields.length}
                onCustomFieldsClick={() => navigate(orderPaths.driverCustomFields(trackCode, isDropoffTracking))}
                callDisabled={isCallDisabled(order.status.code)}
              />
            ) : null
          ) : lastEvent ? (
            <LastEventCard
              event={lastEvent}
              grouped
              eventsCount={eventsCount}
              onEventsClick={() => navigate(orderPaths.events(trackCode, isDropoffTracking))}
            />
          ) : null}

          {order.pickup ? (
            <StopRow
              kind="pickup"
              address={order.pickup.address}
              completedAt={order.pickup.completedAt}
              detailsCount={order.pickup.detailsCount}
              onClick={() => navigate(orderPaths.pickup(trackCode, isDropoffTracking))}
            />
          ) : null}
          {order.dropoffs.map((dropoff, index) => (
            <StopRow
              key={dropoff.id}
              kind="dropoff"
              // No sequence number in dropoff-tracking mode: `dropoffs` has
              // been narrowed to the one tracked stop (see
              // `applyDropoffTracking`), so "Dropoff 1" here would read as
              // an actual route position rather than the placeholder it is.
              sequence={isOnDemand && !isDropoffTracking ? index + 1 : undefined}
              address={dropoff.address}
              completedAt={dropoff.completedAt}
              detailsCount={dropoff.detailsCount}
              onClick={() => navigate(orderPaths.dropoff(trackCode, dropoff.id, isDropoffTracking))}
            />
          ))}

          <EntityInfoRow
            iconUrl={order.service.icon}
            label={t("Service", language)}
            value={order.service.title}
            badgeCount={order.service.options.length + order.customFields.length}
            onClick={() => navigate(orderPaths.serviceDetails(trackCode, isDropoffTracking))}
          />
          {order.vehicleType ? (
            <EntityInfoRow
              iconUrl={order.vehicleType.icon}
              label={t("Vehicle", language)}
              value={order.vehicleType.title}
              badgeCount={order.vehicleType.options.length}
              onClick={() => navigate(orderPaths.vehicleDetails(trackCode, isDropoffTracking))}
            />
          ) : null}
        </>
      ) : null}
    </PageShell>
  );
};
